/**
 * Cookie-aware fetch helpers for the /v1/admin/* surface.
 *
 * Mirrors the shape of ../auth/api.ts: includes credentials,
 * attaches CSRF, exposes typed methods. Sensitive platform-owner
 * actions accept an optional ``mfaCode`` argument that the SPA
 * collects via the MFA-prompt modal and threads through to the
 * X-CVP-MFA-Code header.
 */

import { loadConfig } from "../../api/client";

const COOKIE_NAME_CSRF = "cvp_csrf";

interface AdminApiError {
  status: number;
  code: string;
  message: string;
}

function readCookie(name: string): string | null {
  const all = document.cookie.split(";");
  for (const raw of all) {
    const trimmed = raw.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

async function adminFetch<T>(
  path: string,
  init?: RequestInit & { mfaCode?: string },
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const method = (init?.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = readCookie(COOKIE_NAME_CSRF);
    if (csrf !== null) {
      headers.set("X-CVP-CSRF", csrf);
    }
  }
  if (init?.mfaCode !== undefined) {
    headers.set("X-CVP-MFA-Code", init.mfaCode);
  }
  const cfg = loadConfig();
  const response = await fetch(`${cfg.baseUrl}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  const body = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const err: AdminApiError = {
      status: response.status,
      code: body?.code ?? "error",
      message: body?.message ?? response.statusText,
    };
    throw err;
  }
  return body as T;
}

async function adminUpload<T>(
  path: string,
  file: File,
  options?: { mfaCode?: string },
): Promise<T> {
  const headers = new Headers();
  const csrf = readCookie(COOKIE_NAME_CSRF);
  if (csrf !== null) {
    headers.set("X-CVP-CSRF", csrf);
  }
  if (options?.mfaCode !== undefined) {
    headers.set("X-CVP-MFA-Code", options.mfaCode);
  }
  const form = new FormData();
  form.append("file", file);
  const cfg = loadConfig();
  const response = await fetch(`${cfg.baseUrl}${path}`, {
    method: "POST",
    headers,
    body: form,
    credentials: "include",
  });
  const text = await response.text();
  const body = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const err: AdminApiError = {
      status: response.status,
      code: body?.code ?? "error",
      message: body?.message ?? response.statusText,
    };
    throw err;
  }
  return body as T;
}

export type { AdminApiError };

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  suspended: boolean;
  member_count: number;
  created_at: string;
}

export interface PlatformBrandingPayload {
  product_name: string;
  marketing_url: string | null;
  root_logo_url: string | null;
  default_support_contact: string | null;
  default_tenant_theme: Record<string, string> | null;
}

export interface CustomDomainSummary {
  id: string;
  tenant_id: string;
  hostname: string;
  verification_token: string;
  verification_expires_at: string;
  verified: boolean;
}

export interface CustomDomainVerifyResult {
  id: string;
  hostname: string;
  verified: boolean;
  verified_at: string | null;
}

export interface LlmProviderConfigOut {
  role: "primary" | "fallback";
  provider: "anthropic" | "openai";
  model_id: string;
  enabled: boolean;
  key_fingerprint: string;
  updated_at: string | null;
}

export interface LlmProviderConfigIn {
  provider: "anthropic" | "openai";
  api_key: string;
  model_id: string;
  enabled: boolean;
}

export interface LlmModelRate {
  model_id: string;
  input_micros_per_mtok: number;
  output_micros_per_mtok: number;
  updated_at: string;
}

export interface LlmConfigTestResult {
  ok: boolean;
  error: string | null;
  sample_text: string | null;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
}

export interface LlmDashboardCall {
  occurred_at: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
  cost_aud_micros: number;
}

export interface LlmDashboardModelSpend {
  model: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_aud_micros: number;
}

export interface Member {
  user_id: string;
  email: string;
  display_name: string | null;
  role: "platform_owner" | "account_admin" | "member";
  member_since: string;
}

export interface MembersResponse {
  members: Member[];
}

export interface InviteIn {
  email: string;
  role: "platform_owner" | "account_admin" | "member";
  initial_password: string;
}

export interface LlmDashboard {
  window_start: string;
  window_end: string;
  active_primary: LlmProviderConfigOut | null;
  active_fallback: LlmProviderConfigOut | null;
  total_calls: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_aud_micros: number;
  average_input_tokens: number;
  average_output_tokens: number;
  average_latency_ms: number;
  average_cost_aud_micros: number;
  spend_by_model: LlmDashboardModelSpend[];
  recent_calls: LlmDashboardCall[];
  models_missing_rates: string[];
}

export const adminApi = {
  // ---- Platform-owner ----
  listTenants: (): Promise<{ tenants: TenantSummary[] }> =>
    adminFetch("/v1/admin/platform/tenants"),

  provisionTenant: (name: string, slug: string): Promise<TenantSummary> =>
    adminFetch("/v1/admin/platform/tenants", {
      method: "POST",
      body: JSON.stringify({ name, slug }),
    }),

  suspendTenant: (id: string, mfaCode: string): Promise<void> =>
    adminFetch(`/v1/admin/platform/tenants/${encodeURIComponent(id)}/suspend`, {
      method: "POST",
      mfaCode,
    }),

  reactivateTenant: (id: string, mfaCode: string): Promise<void> =>
    adminFetch(`/v1/admin/platform/tenants/${encodeURIComponent(id)}/reactivate`, {
      method: "POST",
      mfaCode,
    }),

  deleteTenant: (id: string, mfaCode: string): Promise<void> =>
    adminFetch(`/v1/admin/platform/tenants/${encodeURIComponent(id)}`, {
      method: "DELETE",
      mfaCode,
    }),

  getPlatformBranding: (): Promise<PlatformBrandingPayload> =>
    adminFetch("/v1/admin/platform/branding"),

  putPlatformBranding: (
    payload: PlatformBrandingPayload,
    mfaCode: string,
  ): Promise<PlatformBrandingPayload> =>
    adminFetch("/v1/admin/platform/branding", {
      method: "PUT",
      body: JSON.stringify(payload),
      mfaCode,
    }),

  uploadPlatformAsset: (
    kind: "logo" | "favicon",
    file: File,
    mfaCode: string,
  ): Promise<PlatformBrandingPayload> =>
    adminUpload(`/v1/admin/platform/branding/${kind}`, file, { mfaCode }),

  registerCustomDomain: (
    tenantId: string,
    hostname: string,
  ): Promise<CustomDomainSummary> =>
    adminFetch("/v1/admin/platform/custom-domains", {
      method: "POST",
      body: JSON.stringify({ tenant_id: tenantId, hostname }),
    }),

  verifyCustomDomain: (
    id: string,
    dnsRecord: string,
  ): Promise<CustomDomainVerifyResult> =>
    adminFetch(`/v1/admin/platform/custom-domains/${encodeURIComponent(id)}/verify`, {
      method: "POST",
      headers: { "X-CVP-DNS-Record": dnsRecord },
    }),

  crossTenantAudit: (): Promise<{
    events: Array<{
      event_id: string;
      tenant_id: string | null;
      kind: string;
      occurred_at: string;
      user_id: string | null;
      service_account_id: string | null;
      request_id: string | null;
      payload: unknown;
    }>;
  }> => adminFetch("/v1/admin/platform/audit"),

  // ---- LLM provider configuration + dashboard ----
  listLlmConfigs: (): Promise<{ items: LlmProviderConfigOut[] }> =>
    adminFetch("/v1/admin/platform/llm/config"),

  putLlmConfig: (
    role: "primary" | "fallback",
    body: LlmProviderConfigIn,
    mfaCode: string,
  ): Promise<LlmProviderConfigOut> =>
    adminFetch(`/v1/admin/platform/llm/config/${role}`, {
      method: "PUT",
      body: JSON.stringify(body),
      mfaCode,
    }),

  deleteLlmConfig: (
    role: "primary" | "fallback",
    mfaCode: string,
  ): Promise<void> =>
    adminFetch(`/v1/admin/platform/llm/config/${role}`, {
      method: "DELETE",
      mfaCode,
    }),

  testLlmConfig: (
    role: "primary" | "fallback",
    prompt: string,
  ): Promise<LlmConfigTestResult> =>
    adminFetch(`/v1/admin/platform/llm/config/${role}/test`, {
      method: "POST",
      body: JSON.stringify({ prompt }),
    }),

  listLlmRates: (): Promise<{ items: LlmModelRate[] }> =>
    adminFetch("/v1/admin/platform/llm/rates"),

  putLlmRate: (rate: LlmModelRate): Promise<LlmModelRate> =>
    adminFetch(`/v1/admin/platform/llm/rates/${encodeURIComponent(rate.model_id)}`, {
      method: "PUT",
      body: JSON.stringify(rate),
    }),

  llmDashboard: (days: number): Promise<LlmDashboard> =>
    adminFetch(`/v1/admin/platform/llm/dashboard?days=${days}`),

  // ---- Member management (account-admin surface) ----
  listMembers: (): Promise<MembersResponse> =>
    adminFetch("/v1/admin/account/members"),

  inviteMember: (body: InviteIn): Promise<Member> =>
    adminFetch("/v1/admin/account/members", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  changeMemberRole: (
    userId: string,
    role: "platform_owner" | "account_admin" | "member",
  ): Promise<Member> =>
    adminFetch(`/v1/admin/account/members/${encodeURIComponent(userId)}/role`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),

  removeMember: (userId: string): Promise<void> =>
    adminFetch(`/v1/admin/account/members/${encodeURIComponent(userId)}`, {
      method: "DELETE",
    }),
};
